import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type {
  Questionnaire_Template_Version_Status_Enum,
  UpdateQuestionnaireTemplateVersionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateQuestionnaireTemplateVersionInput {
  Id: string;
  Version: string;
  Status: Questionnaire_Template_Version_Status_Enum;
  Schema: JSONB;
  UISchema: JSONB;
  CustomAttributeData?: Record<string, unknown> | null;
}

/**
 * The tRPC `update` procedure returns `{ affected_rows }`, but the
 * GraphQL mutation returns the updated row's `Id`. Consumers only
 * `await` the call (they do not read the result), so we reshape to
 * the GraphQL shape using the `Id` that was submitted.
 */
const mapTrpcResponseToGraphQL = (
  id: string
): UpdateQuestionnaireTemplateVersionMutation => ({
  update_questionnaire_template_version_by_pk: {
    __typename: 'questionnaire_template_version',
    Id: id,
  },
});

export const useUpdateQuestionnaireTemplateVersionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.questionnaireTemplateVersion.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplateVersion.pathKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplate.pathKey(),
        });
      },
    }),
  });

  return {
    updateQuestionnaireTemplateVersion: async (
      input: UpdateQuestionnaireTemplateVersionInput
    ): Promise<UpdateQuestionnaireTemplateVersionMutation> => {
      await mutation.mutateAsync({
        Id: input.Id,
        Version: input.Version,
        Status: input.Status,
        Schema: input.Schema,
        UISchema: input.UISchema,
        CustomAttributeData: input.CustomAttributeData ?? null,
      });

      return mapTrpcResponseToGraphQL(input.Id);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
